import clsx from 'clsx';
import { assign, flattenDeep, has, isEmpty, isEqual, isFunction, map, pick, xor } from 'lodash';
import type { FormFieldWrapperProps } from '@/formbuilder/FormFieldWrapper';
import type { FormElement, ValidatingFormComponent } from '@/formbuilder/formBuilder.constants';

export function getFormFieldProps(
  formState: { submitFailed?: boolean },
  input: { onChange?: (value: unknown) => void; value?: unknown },
  meta: { submitFailed?: boolean; error?: unknown; modified?: boolean },
  props: any,
): any {
  const { extraClassNames, testId, value, skipToFormState = false, defaultProvided = false } = props;
  const showError = Boolean(
    formState.submitFailed || meta.submitFailed ? meta.error : meta.error && (meta.modified || defaultProvided),
  );

  return {
    ...props,
    'onChange': (newValue: unknown) => {
      props.onChange?.(newValue);
      input.onChange?.(newValue);
    },
    'value': skipToFormState ? input.value : value,
    'data-testid': testId,
    // temporary until all Forms are converted to FormBuilder (https://seeq.atlassian.net/browse/CRAB-21727)
    'fromFormBuilder': true as const,
    'className': clsx({ 'error-border': showError }),
    'extraClassNames': clsx(extraClassNames, {
      'error-border': showError,
    }),
    showError,
  };
}

export function getFormFieldWrapperProps<T = unknown>(
  props: ValidatingFormComponent<T>,
  propsToOmit?: (keyof FormFieldWrapperProps)[],
): Omit<FormFieldWrapperProps, 'children'> {
  const propsToInclude = [
    'testId',
    'label',
    'wrapperClassNames',
    'customErrorText',
    'customErrorParams',
    'tooltip',
    'tooltipPlacement',
    'onTooltipClick',
    'popoverContent',
    'id',
    'warningText',
  ];
  const selectedProps = (propsToOmit?.length ?? 0) > 0 ? xor(propsToInclude, propsToOmit) : propsToInclude;

  return pick(props, selectedProps as (keyof ValidatingFormComponent<T>)[]) as Omit<FormFieldWrapperProps, 'children'>;
}

export function getValidationFunction<T>(
  defaultValidation: (value: T) => boolean,
  extendValidation: boolean | undefined,
  customValidation?: (value: T) => boolean,
) {
  if (extendValidation && isFunction(customValidation)) {
    return (value: T) => defaultValidation(value) || customValidation(value);
  }

  return isFunction(customValidation) ? customValidation : defaultValidation;
}

/**
 * Recursively "flattens" out all form elements into an array of form field names.
 **/
export function flattenFormDefinition(formDefinition: FormElement[]): FormElement[] {
  const flattenFormDefinitionInternal = (defs: FormElement[]): unknown[] =>
    map(defs, (def) =>
      (def.component === 'FormGroup' ||
        def.component === 'FormGroupWithTitle' ||
        def.component === 'AdvancedFormGroup' ||
        def.component === 'FormRow' ||
        def.component === 'RemovableFormRow' ||
        def.component === 'RemovableFormGroup' ||
        def.component === 'DraggableCollapsibleFormBlock' ||
        def.component === 'CollapsibleFormGroup') &&
      'components' in def &&
      def.components
        ? flattenFormDefinitionInternal(def.components)
        : def,
    );
  const flattened = flattenFormDefinitionInternal(formDefinition);
  return flattenDeep(flattened) as FormElement[];
}

export function decorateWithFieldNames(formDefinition: FormElement[]) {
  const addFieldNames = (formDefinition: FormElement[]): FormElement[] =>
    map(formDefinition, (def): FormElement => {
      const fieldNames = map(flattenFormDefinition([def]), 'name');
      const defWithChildren =
        'components' in def && !isEmpty((def as { components?: FormElement[] }).components)
          ? { ...def, components: addFieldNames((def as { components?: FormElement[] }).components as FormElement[]) }
          : def;

      return assign({}, defWithChildren, { fieldNames });
    });

  return addFieldNames(formDefinition);
}

// walks the formConfig and assigns the appropriate numbers for checkmarkAndNumber display
export function decorateWithNumbers(formDefinition: FormElement[]) {
  let number = 0;

  const hasDisplayNumber = (def: FormElement) =>
    def.displayNumber && (!has(def, 'includeIf') || (def as any).includeIf);

  const getDefWithNumber = (def: FormElement) => {
    number++;
    return assign({}, def, { number });
  };

  const addDisplayNumber = (formDefinition: FormElement[]): FormElement[] =>
    map(formDefinition, (def: FormElement) => {
      const hasComponents = 'components' in def && Array.isArray((def as { components?: FormElement[] }).components);
      if (hasDisplayNumber(def)) {
        const decoratedDef = getDefWithNumber(def);
        if (hasComponents && !isEmpty((def as { components?: FormElement[] }).components)) {
          assign(decoratedDef, {
            components: addDisplayNumber((def as { components?: FormElement[] }).components as FormElement[]),
          });
        }
        return decoratedDef;
      } else {
        if (
          hasComponents &&
          !isEmpty((def as { components?: FormElement[] }).components) &&
          (!has(def, 'includeIf') || (def as any).includeIf)
        ) {
          return assign({}, def, {
            components: addDisplayNumber((def as { components?: FormElement[] }).components as FormElement[]),
          });
        }
        return def;
      }
    });
  return addDisplayNumber(formDefinition);
}

type MemoComparableProps = {
  value?: unknown;
  disabled?: boolean;
  extendValidation?: boolean;
  extraClassNames?: string;
  options?: unknown;
};

export function canUseMemo(prev: MemoComparableProps, next: MemoComparableProps) {
  // return false if render is necessary
  return !(
    !isEqual(prev.value, next.value) ||
    prev.disabled !== next.disabled ||
    prev.extendValidation ||
    prev.extraClassNames !== next.extraClassNames ||
    prev.options !== next.options
  );
}
